/* eslint-disable react/no-unescaped-entities */
/*
 * HomePage
 *
 * This is the first thing users see of our App, at the '/' route
 */

import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import ReactResizeDetector from 'react-resize-detector';
import { uniqueId } from 'lodash';
import Collapsible from 'react-collapsible';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';

import injectReducer from 'utils/injectReducer';
import injectSaga from 'utils/injectSaga';
import { makeSelectProgressBarStatus } from 'containers/ProgressBar/selectors';
import H2 from 'components/H2';
import InlineAppErrorText from 'containers/AppError/index';
import { makeSelectIsAuthenticated } from 'containers/App/selectors';
import HelpText from 'components/HelpText';

import messages from './messages';
import {
  handleFormDataChange,
  resetLoginInfo,
  toggleModalError,
  validatePolicyInfo,
  validateVerificationCode,
} from './actions';
import {
  makeSelectAuthToken,
  makeSelectErrors,
  makeSelectLoginInfo,
  makeSelectRequireVerificationCode,
  makeToggleModal,
} from './selectors';

import reducer from './reducer';
import saga from './saga';
import Section from '../FastCover/components/Section';
import {
  LoginBottom,
  LoginTop,
  TextInput,
  Wrapper,
} from './components';
import FlexBox from '../../components/FlexBox/index';
import {
  ErrorMessage,
  InLineValidate,
  ValidateGroup,
} from '../../components/Validation/';
import { required } from '../../utils/validation/validator';
import ModalError from './components/ModalErrors';
import FAQ from './components/FAQ';
import AsyncButton from '../../components/AsyncButton/index';
import postMessage, { updateParentDomain } from '../../utils/postMessage';
import { RESIZE } from '../../utils/constants';
import PageSectionHeader from '../FastCover/components/PageSectionHeader';
import FNOLTooltip from '../../components/FNOLTooltip';
import Asterisk from '../../components/Asterisk';
import Step6Shared from '../FastCover/Step6/components/Step6Shared';
import Step7Shared from '../FastCover/Step7/components/Step7Shared';
import Step8Shared from '../FastCover/Step8/components/Step8Shared';
import Step9Shared from '../FastCover/Step9/Step9Shared';
import Step11Shared from '../FastCover/Step11/components/Step11Shared';

export class OAuth extends React.PureComponent { // eslint-disable-line react/prefer-stateless-function

  constructor(props) {
    super(props);
    this.submit = this.submit.bind(this);
    this.validatorGroup = new ValidateGroup();

    this.state = {
      errors: [],
      clickedItemId: '',
      loginButtonId: uniqueId(),
      isFirstQuestionOpen: false,
      secondQuestionOpen: false,
      thirdQuestionOpen: false,
    };

    this.onResize = this.onResize.bind(this);
  }

  componentWillMount() {
    updateParentDomain();
  }

  componentDidMount() {
    postMessage(RESIZE);
  }

  componentWillReceiveProps(nextProps) {
    // the validator group needs to be cleared because the Verification Code field is
    // rendered at runtime
    if (this.props.requireVerificationCode !== nextProps.requireVerificationCode) {
      this.validatorGroup.clearValidatorInGroup();
    }
    if (!nextProps.loading) {
      this.setState({ clickedItemId: '' });
    }
  }

  onResize = () => {
    postMessage(RESIZE);
  };

  handleChangeCollapsible = () => {
    postMessage(RESIZE);
  };

  handleChange = (propertyName) => (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    this.props.onFormDataChange(propertyName, value);
  };

  handleClick = (event) => {
    const target = event.currentTarget || event.srcElement;
    this.setState({ clickedItemId: target.id });
    this.submit();
  };

  submit() {
    const { requireVerificationCode } = this.props;
    const { validationCounter = 0 } = this.state;
    if (this.validatorGroup.checkValidation()) {
      if (requireVerificationCode) {
        this.props.onValidateVerificationCode();
      } else {
        this.props.onValidatePolicyInfo();
      }
    } else {
      this.setState({ validationCounter: validationCounter + 1 });
    }
  }

  showSpinnerForElement = (elementId) =>
    this.state.clickedItemId === elementId;

  renderVerificationCodeSection = () =>
    (<Section>
      <HelpText>
        <FormattedMessage {...messages.verificationEmailSent} />
      </HelpText>
      <InLineValidate
        name="verificationCode"
        validators={[required]}
        group={this.validatorGroup}
        addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
        validChangeInGroup={this.validatorGroup.validChangeInGroup}
        validationCounter={this.state.validationCounter}
        showErrorMessage
      >
        <TextInput
          name="verificationCode"
          type="text"
          onChange={this.handleChange('form.verificationCode')}
          placeholder="Verification Code (e.g. 002200)"
        />
        <ErrorMessage for="required">Verification code is required</ErrorMessage>
      </InLineValidate>
    </Section>);

  render() {
    const { errors, loginButtonId } = this.state;
    const {
      error,
      requireVerificationCode,
      loading,
    } = this.props;
    const oauthErrors = error ? [{ message: this.props.error.message }] : errors;
    const verificationCodeSection = (requireVerificationCode) ? this.renderVerificationCodeSection() : '';
    const buttonTxt = (requireVerificationCode) ? { ...messages.proceed } : { ...messages.login };
    return (
      <div className="d-flex justify-content-center flex-column align-content-center">
        <Wrapper className="mb-0">
          <ModalError
            {...this.props}
            data={oauthErrors}
            isOpen={this.props.modalState}
          />
          <LoginTop>
            <H2><FormattedMessage {...messages.pageTitle} /></H2>
          </LoginTop>
          <LoginBottom>
            <Section>
              <div className="text-center" style={{ marginBottom: '15px' }}>
                <HelpText style={{ fontSize: '16px' }}>
                  <FormattedMessage {...messages.instruction} />
                </HelpText>
              </div>
              <InlineAppErrorText {...this.props} />

              <InLineValidate
                name="policyNumber"
                validators={[required]}
                group={this.validatorGroup}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                showErrorMessage
              >
                <PageSectionHeader> <FormattedMessage {...messages.policyNumber} />&nbsp;<Asterisk />&nbsp;<FNOLTooltip dataFor={'policyNumberHint'} content={'You can find your policy number in your confirmation email <br/>or on your Certificate of Insurance.'} /></PageSectionHeader>
                <TextInput
                  name="policyNumber"
                  type="text"
                  onChange={this.handleChange('form.policyNumber')}
                  placeholder="Policy Number"
                  disabled={requireVerificationCode}
                  value={this.props.form.policyNumber}
                />
                <ErrorMessage for="required">Policy Number is required</ErrorMessage>
              </InLineValidate>
            </Section>
            <Section>
              <InLineValidate
                name="emailId"
                validators={[required]}
                group={this.validatorGroup}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                showErrorMessage
              >
                <PageSectionHeader><FormattedMessage {...messages.emailAddress} />&nbsp;<Asterisk /></PageSectionHeader>
                <TextInput
                  name="emailId"
                  type="text"
                  onChange={this.handleChange('form.email')}
                  placeholder="Email address used to purchase policy"
                  disabled={requireVerificationCode}
                  value={this.props.form.email}
                />
                <ErrorMessage for="required">Email is required</ErrorMessage>
              </InLineValidate>
            </Section>
            {verificationCodeSection}
            <Section>
              <FlexBox justifyContent="center">
                <AsyncButton
                  id={loginButtonId}
                  className="btn-authentication"
                  onClick={this.handleClick}
                  loading={loading}
                  spinner={loading && this.showSpinnerForElement(loginButtonId)}
                >
                  <FormattedMessage {...buttonTxt} />
                </AsyncButton>
              </FlexBox>
            </Section>
          </LoginBottom>
          <ReactResizeDetector handleHeight onResize={this.onResize} />
        </Wrapper>
        <div
          id="faq"
          className="d-flex flex-column justify-content-center align-items-center"
        >
          <Wrapper noBox>
            <FAQ>FAQs</FAQ>
            <Collapsible
              transitionTime={100}
              open={this.state.secondQuestionOpen}
              onClosing={() => this.setState({ secondQuestionOpen: false })}
              onOpening={() => this.setState({ secondQuestionOpen: true, isFirstQuestionOpen: false, thirdQuestionOpen: false })}
              onClose={() => { this.handleChangeCollapsible(); }}
              onOpen={() => { this.handleChangeCollapsible(); }}
              trigger="Q. What can I expect once my claim has been submitted?"
            >
              <div className="p-3 text-justify">
                <p>
            Once your claim is submitted along with your supporting documentation, you will instantly receive a unique Claim Number and an email confirmation.</p>
                <p>Just like our customers, no two travel claims are the same. In some cases, if you have provided all the documents we need, we'll be in touch about your claim outcome within 10 business days.</p>
                <p> But, if we need extra documentation or information, we'll also contact you within the same timeframe. To help speed the process up, we've outlined what documents we need for each type of claim in the list below and in the online claim form. </p>
                <p>If your claim is approved, you should receive payment within 5 business days.</p>
              </div>
            </Collapsible>
            <Collapsible
              transitionTime={100}
              onOpening={() => this.setState({ isFirstQuestionOpen: true, secondQuestionOpen: false, thirdQuestionOpen: false })}
              onClosing={() => this.setState({ isFirstQuestionOpen: false })}
              open={this.state.isFirstQuestionOpen}
              onClose={() => { this.handleChangeCollapsible(); }}
              onOpen={() => { this.handleChangeCollapsible(); }}
              trigger="Q. What supporting documents do I need for my claim?"
            >
              <Tabs
                onSelect={() => {
                  setTimeout(() => {
                    this.handleChangeCollapsible();
                  }, 300);
                }}
              >
                <TabList>
                  <Tab>
                    <div className="d-flex align-items-center justify-content-center h-100">
                      <div><img alt={'benefit logo'} className={'d-inline'} width={30} src={'/Medical-gray-h.png'} /></div>
                      <div className="ml-lg-2 ml-md-2 ml-sm-0 d-flex flex-column text-center">
                        <span>Overseas</span>
                        <span>Medical</span>
                      </div>
                    </div>
                  </Tab>
                  <Tab>
                    <div className="d-flex align-items-center justify-content-center h-100">
                      <div><img alt={'benefit logo'} className={'d-inline'} width={50} src={'/Luggage-gray.png'} /></div>
                      <div className="ml-lg-2 ml-md-2 ml-sm-0 d-flex flex-column text-center">
                        <span>Personal</span>
                        <span>Items</span>
                      </div>
                    </div>
                  </Tab>
                  <Tab>
                    <div className="d-flex align-items-center justify-content-center h-100">
                      <div className="d-flex  align-items-center justify-content-center h-100">
                        <div><img alt={'benefit logo'} className={'d-inline'} width={40} src={'/Trip-Cancellation-New-gray-h.png'} /></div>
                        <div className="ml-lg-2 ml-md-2 ml-sm-0 d-flex flex-column text-center">
                          <span>Cancellation/</span>
                          <span>Additional Expenses</span>
                        </div>
                      </div>
                    </div>
                  </Tab>
                  <Tab>
                    <div className="d-flex align-items-center justify-content-center h-100">
                      <div><img alt={'benefit logo'} className={'d-inline'} width={50} src={'/Trip-Distruption-gray.png'} /></div>
                      <div className="ml-lg-2 ml-md-2 ml-sm-0 d-flex flex-column text-center">
                        <span>Rental</span>
                        <span>Vehicle</span>
                      </div>
                    </div>
                  </Tab>
                  <Tab>
                    <div className="d-flex align-items-center justify-content-center h-100">
                      <div><img alt={'benefit logo'} className={'d-inline'} width={30} src={'/Other-gray-h.png'} /></div>
                      <div className="ml-lg-2 ml-md-2 ml-sm-0 d-flex flex-column text-center">
                        <span>Other</span>
                      </div>
                    </div>
                  </Tab>
                </TabList>
                <TabPanel>
                  <div>
                    <Step8Shared />
                  </div>
                </TabPanel>
                <TabPanel>
                  <div>
                    <Step7Shared />
                  </div>
                </TabPanel>
                <TabPanel>
                  <div>
                    <Step6Shared />
                  </div>
                </TabPanel>


                <TabPanel>
                  <div>
                    <Step9Shared />
                  </div>
                </TabPanel>
                <TabPanel>
                  <div className="p-3">
                    <Step11Shared />
                  </div>
                </TabPanel>
              </Tabs>
            </Collapsible>

            <Collapsible
              transitionTime={100}
              open={this.state.thirdQuestionOpen}
              onClosing={() => this.setState({ thirdQuestionOpen: false })}
              onOpening={() => this.setState({ thirdQuestionOpen: true, isFirstQuestionOpen: false, secondQuestionOpen: false })}
              onClose={() => { this.handleChangeCollapsible(); }}
              onOpen={() => { this.handleChangeCollapsible(); }}
              trigger="Q. Can I lodge a claim if I don't have all the supporting documents?"
            >
              <div className="p-3 text-justify">
                <p>The more documentation you can provide, the faster we can assess your claim. But, don't panic! We understand that sometimes you are unable to provide some of the documentation we need.</p>
                <p>What you can do is lodge a claim right now and provide any missing documents at a later stage. Just let us know why you are unable to provide these documents when you complete the online claim form.</p>
              </div>
            </Collapsible>
          </Wrapper>
        </div>
      </div>
    );
  }
}

OAuth.propTypes = {
  onValidateVerificationCode: PropTypes.func,
  onValidatePolicyInfo: PropTypes.func,
  onFormDataChange: PropTypes.func,
  error: PropTypes.object,
  modalState: PropTypes.bool,
  requireVerificationCode: PropTypes.bool,
  form: PropTypes.object,
  loading: PropTypes.bool,
};

export function mapDispatchToProps(dispatch) {
  return {
    onValidatePolicyInfo: () => dispatch(validatePolicyInfo()),
    onValidateVerificationCode: () => dispatch(validateVerificationCode()),
    onFormDataChange: (propertyName, value) =>
      dispatch(handleFormDataChange(propertyName, value)),
    toggleModalError: () => dispatch(toggleModalError()),
    onReset: () => dispatch(resetLoginInfo()),
  };
}

const mapStateToProps = createStructuredSelector({
  token: makeSelectAuthToken(),
  loading: makeSelectProgressBarStatus(),
  error: makeSelectErrors(),
  modalState: makeToggleModal(),
  authenticated: makeSelectIsAuthenticated(),
  requireVerificationCode: makeSelectRequireVerificationCode(),
  form: makeSelectLoginInfo(),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

const withReducer = injectReducer({
  key: 'oauth',
  reducer,
});
const withSaga = injectSaga({
  key: 'oauth',
  saga,
});

export default compose(
  withReducer,
  withSaga,
  withConnect,
)(OAuth);



// WEBPACK FOOTER //
// ./app/containers/Login/index.js